import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BsChevronDown, BsChevronExpand, BsChevronUp } from 'react-icons/bs'

/** The sorting slice of a react-table column instance. The tables in this
 *  folder pass their columns untyped, so this describes only what the header
 *  actually reads rather than dragging in the generic `HeaderGroup` type. */
export interface SortableColumn {
  isSorted: boolean
  isSortedDesc?: boolean
  render: (type: string) => ReactNode
  getSortByToggleProps: () => { onClick?: (e: unknown) => void }
}

interface Props {
  column: SortableColumn
}

/**
 * A column header that sorts when activated. It is a button rather than a
 * clickable `<th>` so the tables stay usable from the keyboard, and the idle
 * chevron is drawn rather than hidden until hover so it is visible which
 * columns sort at all.
 */
const SortableHeader = ({ column }: Props) => {
  const { t } = useTranslation()
  // Only the handler: react-table also returns an untranslated
  // "Toggle SortBy" title and an inline cursor style, both replaced here.
  const { onClick } = column.getSortByToggleProps()
  const { isSorted, isSortedDesc } = column

  return (
    <button
      type='button'
      onClick={onClick}
      title={t('sort')}
      className='inline-flex items-center gap-1.5 font-bold rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryBlue/40'
    >
      {column.render('Header')}
      <span
        aria-hidden='true'
        className={`text-xs transition-colors ${
          isSorted ? 'text-primaryBlue' : 'text-lightGray/40'
        }`}
      >
        {isSorted ? (
          isSortedDesc ? (
            <BsChevronDown />
          ) : (
            <BsChevronUp />
          )
        ) : (
          <BsChevronExpand />
        )}
      </span>
    </button>
  )
}

export default SortableHeader
